import { ArrowLeftOutlined, DownOutlined } from '@ant-design/icons';
import { T, useTolgee, useTranslate } from '@tolgee/react';
import { Button, DatePicker, Popover, Select, Tooltip } from 'antd';
import classNames from 'classnames';
import dayjs, { Dayjs } from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import React, { useEffect, useState } from 'react';
import CartIcon from '../../../assets/icons/car.svg';
import CartIconDark from '../../../assets/icons/car2.svg';
import {
  useGlobalActions,
  useGlobalContext,
} from '../../../global-context/GlobalContext';
import isoWeek from 'dayjs/plugin/isoWeek';

import useIsMobile from '../../../hooks/utils/useIsMobile';
import {
  getSpecialOrderDefaultDate,
  isEditableDate,
} from '../../../utils/calendarUtility';
import { showToast } from '../../../utils/commonUtility';
import './SpecialOrderHeader.scss';

dayjs.extend(isoWeek);
dayjs.extend(customParseFormat);

type SpecialOrderHeaderProps = {
  onDateSelect: (dateObj: any) => void;
  selectedDate: any;
  toggleOrderSectionVisibility: React.Dispatch<React.SetStateAction<boolean>>;
  orderSectionVisible: boolean;
};

const SpecialOrderHeader: React.FC<SpecialOrderHeaderProps> = ({
  onDateSelect,
  selectedDate,
  toggleOrderSectionVisibility,
  orderSectionVisible,
}) => {
  const isMobile = useIsMobile();
  const [weekDays, setWeekDays] = useState<any[]>([]);
  const {
    itemsInCart: cartCount,
    specialOrderId,
    isExistingCartUpdated,
    cutoffHours,
  } = useGlobalContext(c => c.specialOrder);
  const { clearCart } = useGlobalActions();
  const { getLanguage } = useTolgee();
  const language = getLanguage() || 'fr';

  const [datePopupOpenKey, setDatePopupOpenKey] = useState(null);
  const { t } = useTranslate();

  const generateWeekDates = (date: Dayjs) => {
    const monday = date.isoWeekday(1);
    const weekDatesArray: any[] = [];
    for (let i = 0; i < 5; i++) {
      const currentDay = monday.add(i, 'day');

      weekDatesArray.push({
        title: currentDay,
        day: i + 1,
        disabled: currentDay.isBefore(dayjs(), 'day'),
        date: currentDay.toISOString(),
      });
    }
    return weekDatesArray;
  };

  const handleDateSelect = (date: Dayjs | null) => {
    if (!date) {
      console.error('No date selected');
      return;
    }

    const weekDates = generateWeekDates(date.locale(language));
    const ValidDate = weekDates.find(d => d.date === date.toISOString());
    if (ValidDate) {
      setWeekDays(weekDates);
      onDateSelect(ValidDate);
    } else {
      setWeekDays(weekDates);
      onDateSelect(weekDates[0]);
    }
  };

  const handleCartClick = () => {
    toggleOrderSectionVisibility(prev => !prev);
  };

  const isCartVisible =
    selectedDate && isEditableDate(selectedDate, cutoffHours);
  useEffect(() => {
    const today = getSpecialOrderDefaultDate();
    const weekDates = generateWeekDates(today.locale(language));
    setWeekDays(weekDates);
    const currentDayObject = weekDates.find(
      d => dayjs(d.date).format('YYYY-MM-DD') === today.format('YYYY-MM-DD'),
    );
    if (currentDayObject) {
      onDateSelect(currentDayObject);
    } else {
      onDateSelect(weekDates[0]);
    }
  }, []);

  const handleDateChange = (day: any) => {
    if (day?.disabled) return;
    if (cartCount == 0 || (specialOrderId && !isExistingCartUpdated)) {
      onDateSelect(day);
    } else {
      setDatePopupOpenKey(day.day);
    }
  };
  const handleConfirmDateChange = (day: any) => {
    onDateSelect(day);
    setDatePopupOpenKey(null);
    clearCart();
  };

  const handleCancelDateChange = () => {
    setDatePopupOpenKey(null);
  };

  return (
    <div
      className={classNames('special-order-section-header', {
        mobile: isMobile,
      })}
    >
      {orderSectionVisible && (
        <>
          <DatePicker
            onChange={handleDateSelect}
            picker='week'
            defaultValue={getSpecialOrderDefaultDate().locale(language)}
            disabledDate={current =>
              current && current < dayjs().startOf('day')
            }
            value={dayjs(selectedDate?.date)}
            className='special-order-section-datepicker'
            format={value => {
              const monday = value
                .isoWeekday(1)
                .locale(language)
                .format('ddd. DD/MM/YYYY'); // Monday
              const friday = value
                .isoWeekday(1)
                .locale(language)
                .add(5, 'day')
                .format('ddd. DD/MM/YYYY'); // Friday
              return `${monday} - ${friday}`;
            }}
          />
          {selectedDate && (
            <>
              {isMobile ? (
                <Select
                  className='week-dates-select'
                  value={selectedDate?.day}
                  onChange={value => {
                    const selectedDay = weekDays.find(day => day.day === value);
                    if (selectedDay.disabled) {
                      showToast('previous-date', 'info');
                      return;
                    }
                    handleDateChange(selectedDay);
                  }}
                  options={weekDays.map(day => ({
                    value: day.day,
                    label: day.title.locale(language).format('dddd D MMM'),
                  }))}
                  labelRender={day => {
                    return (
                      <div>
                        {day.label} <DownOutlined />
                      </div>
                    );
                  }}
                />
              ) : (
                <>
                  {weekDays.map((day, index) => (
                    <Tooltip
                      title={
                        day?.disabled
                          ? t('can-not-place-order-for-prior-date')
                          : undefined
                      }
                    >
                      <div
                        key={index}
                        className={classNames(
                          'special-order-section-header__dates',
                          { previousDay: day.disabled },
                        )}
                      >
                        <p onClick={() => handleDateChange(day)}>
                          {day.title.locale(language).format('dddd D MMM')}
                        </p>
                        {selectedDate.day === day.day && (
                          <div
                            className={`tab ${selectedDate.day === day.day ? 'tabs-active' : ''}`}
                          ></div>
                        )}
                        <Popover
                          content={
                            <div className='popover-content'>
                              <p className='message'>
                                {t('change-date-msg')}
                                {dayjs(selectedDate.date)
                                  .locale(language)
                                  .format('dddd D MMM')}
                              </p>
                              <div className='actions'>
                                <Button
                                  className='no-button'
                                  onClick={() => handleConfirmDateChange(day)}
                                >
                                  {t('no')}
                                </Button>
                                <Button
                                  className='yes-button'
                                  onClick={() => handleCancelDateChange()}
                                >
                                  {t('yes')}
                                </Button>
                              </div>
                            </div>
                          }
                          placement='bottom'
                          arrow={{ pointAtCenter: true }}
                          title=''
                          trigger='click'
                          visible={datePopupOpenKey === day.day} // Control visibility based on record key
                          onOpenChange={visible =>
                            setDatePopupOpenKey(visible ? day.day : null)
                          }
                          className='special-order-section-header__dates-popup'
                        ></Popover>
                      </div>
                    </Tooltip>
                  ))}
                </>
              )}
            </>
          )}
        </>
      )}
      {!orderSectionVisible && (
        <>
          <div className='special-order-title'>
            <span className='title'>
              <T keyName='special-order' />
              {' - '}
            </span>
            <span className='date'>
              {selectedDate.title.locale(language).format('dddd D MMM')}
            </span>
          </div>
          <Button
            className='special-order-section-header__arrow'
            style={{ height: '80%' }}
            shape='circle'
            type='text'
            icon={<ArrowLeftOutlined />}
            onClick={() => toggleOrderSectionVisibility(true)}
          />
        </>
      )}
      {isCartVisible && (
        <div
          className={classNames('special-order-section-header__cart', {
            'ordersection-visible': !orderSectionVisible,
          })}
          onClick={handleCartClick}
        >
          <img src={orderSectionVisible ? CartIcon : CartIconDark} alt='' />
          <p>{cartCount}</p>
        </div>
      )}
    </div>
  );
};

export default SpecialOrderHeader;
